import {
  Fragment,
  Suspense,
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
} from 'react';
import { Bot, Box, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type {
  AgentContextProviderProps,
  ChatMentionProviderProps,
  LaunchMentionProviderProps,
  LaunchMentionCreateProps,
  LaunchMentionEditProps,
  LaunchMentionResult,
} from '@reflex/plugin-api';
import type { MentionMenuHandle } from '@reflex/plugin-chat/web/components/ChatInput';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { Kbd } from '@reflex/ui/components/shared/Kbd';
import { cn } from '@reflex/ui/lib/utils';
import { AgentRunsMentionProvider } from './AgentRunsMentionProvider';
import { SandboxMentionProvider } from './SandboxMentionProvider';
import { parseMentionScope } from './mention-scope';
import type { PluginAttachmentValue } from '@/lib/types';
import { usePlugins } from '@/hooks/config/usePlugins';

/**
 * A request to open an inline "Add new …" create surface, emitted when the user
 * picks the create row inside a mention category. The menu hands the resolved
 * plugin create component up to a stable host (the launch dialog), because the
 * menu itself unmounts as soon as the composer blurs — which is exactly what
 * opening the create modal does.
 */
export interface LaunchMentionCreateRequest {
  /** The plugin's create component, mounted by the host with {@link LaunchMentionCreateProps}. */
  CreateComponent: ComponentType<LaunchMentionCreateProps>;
  /** The row label (e.g. 'Add MCP server'), used for the modal title/analytics. */
  label: string;
  /** The mention query the user had typed, forwarded as the form's name seed. */
  defaultName: string;
}

/**
 * A request to open a category's inline "Edit …" surface for one of its rows,
 * emitted when the user clicks that row's cog. Hoisted to the host for the same
 * reason as {@link LaunchMentionCreateRequest}: opening the modal blurs the
 * composer, which unmounts the menu.
 */
export interface LaunchMentionEditRequest {
  /** The plugin's edit component, mounted by the host with {@link LaunchMentionEditProps}. */
  EditComponent: ComponentType<LaunchMentionEditProps>;
  /** The provider's own id for the row being edited. */
  itemId: string;
}

export interface LaunchMentionMenuProps {
  /** The mention query (text after `@`), trimmed. */
  query: string;
  /** Fixed-position style anchoring the menu above the composer. */
  style: CSSProperties;
  /** Attachment configs already applied, so providers can hide in-use items. */
  attachments: Record<string, PluginAttachmentValue>;
  /** The persona currently applied to the run, or `null`. */
  selectedPersonaId: string | null;
  /** Apply a selected result to the launch config. */
  onApply: (result: LaunchMentionResult) => void;
  /** Close the menu without applying anything. */
  onClose: () => void;
  /**
   * Open an inline "Add new …" create surface for a category. The menu emits
   * this when the user picks a category's create row; the host mounts the
   * plugin create component at a stable level (see {@link LaunchMentionCreateRequest}).
   * When omitted, categories render no create row.
   */
  onRequestCreate?: (request: LaunchMentionCreateRequest) => void;
  /**
   * Open a category's inline "Edit …" surface for one row, hosted at the same
   * stable level as {@link onRequestCreate}. When omitted, rows render no edit
   * affordance.
   */
  onRequestEdit?: (request: LaunchMentionEditRequest) => void;
  /**
   * Clear the composer's `@`-query (keeping the menu open) when drilling out of
   * a category, so the root search starts fresh.
   */
  onResetQuery?: () => void;
  /**
   * Scope the menu to a category by rewriting the composer token to
   * `@<keyword>:`. The rewritten query flows back in as {@link query}, which the
   * menu re-parses to enter the category — so the visible token and the active
   * category never desync.
   */
  onScopeCategory?: (keyword: string) => void;
  /**
   * Reports whether the menu currently has any selectable rows. The composer
   * uses this to auto-close after a multi-word query stops matching anything.
   */
  onResultsChange?: (hasResults: boolean) => void;
  /**
   * Context-only mode (e.g. a running agent's chat): skip the config-provider
   * categories, so `@` only references context — agent runs plus every
   * plugin agent-context category (PRs, tickets, issues, …) — and never run
   * configuration (personas, MCP, repos, gateways).
   */
  contextOnly?: boolean;
  /**
   * With {@link contextOnly}: additionally include the config categories
   * whose provider declares `runtimeAttachable` (today MCP servers). Their
   * `set-attachment` results reach {@link onApply} exactly as in the launch
   * menu; the host translates them into a live attach against the running
   * agent. Ignored outside context-only mode, where every config category
   * is present anyway.
   */
  includeRuntimeConfig?: boolean;
  /**
   * Repo slug (`owner/name`) the composer is working against — the launch
   * form's selected repository or the running agent's attached repo. Handed to
   * agent-context providers so repo-scoped categories (branches) know their
   * repo.
   */
  repoSlug?: string;
  /**
   * The agent the composer is attached to, in `contextOnly` mode. Required to
   * render `chatMentionProviders`, which act on that agent; without it the
   * menu shows context categories and agent runs alone.
   */
  agentId?: string;
}

const VALID_ITEM_SELECTOR = '[cmdk-item]:not([aria-disabled="true"])';
const SELECTED_ITEM_SELECTOR = '[cmdk-item][aria-selected="true"]';
const LIST_SELECTOR = '[data-slot="command-list"]';

/**
 * Per-category budget for the flattened root search, where every config
 * category shares one list and a single source must not crowd out the rest.
 */
const ROOT_SEARCH_LIMIT = 6;

/**
 * Ceiling for a category the user has scoped into. Not a legibility budget like
 * {@link ROOT_SEARCH_LIMIT} — nothing competes for the list here — but a guard
 * against an org with thousands of rows, which no amount of scrolling makes
 * usable. Set above any realistic org, so the list is normally complete and the
 * truncation note stays hidden.
 */
export const CATEGORY_LIMIT = 50;

/** A top-level mention category: a plugin provider or the host's agent runs. */
interface MentionCategory {
  id: string;
  label: string;
  /** Text slug that scopes the menu via `@<keyword>:` (e.g. `repo`). */
  keyword?: string;
  Icon: ComponentType<{ className?: string }>;
  render: (props: LaunchMentionProviderProps) => React.ReactNode;
  /** Label for the inline "Add new …" row, when the category supports creation. */
  createLabel?: string;
  /** The plugin's create component, when the category supports creation. */
  CreateComponent?: ComponentType<LaunchMentionCreateProps>;
  /** The plugin's edit component, when the category's rows are editable in place. */
  EditComponent?: ComponentType<LaunchMentionEditProps>;
  /**
   * An agent-context category (references external items rather than
   * configuring the run). Context categories appear in BOTH the launch menu
   * and the chat's context-only menu.
   */
  isContext?: boolean;
}

/**
 * Visual chrome for the menu's cmdk `Command`: a glassy, rounded popover with
 * a quiet uppercase group heading, a clearly legible selected row, and dense
 * rows that lead with the name. Exported so stories can render the exact same
 * shell.
 *
 * No open/close animation: it opens on every `@` keystroke.
 */
export const MENTION_MENU_CLASS =
  'overflow-hidden rounded-xl border border-border/60 bg-popover/95 text-popover-foreground shadow-md backdrop-blur-xl supports-[backdrop-filter]:bg-popover/80 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted-foreground/60 [&_[cmdk-group]]:px-1 [&_[cmdk-group]]:pb-1 [&_[cmdk-item]]:gap-2 [&_[cmdk-item]]:rounded-md [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-1 [&_[cmdk-item]]:text-xs [&_[cmdk-item]]:transition-colors [&_[cmdk-item][aria-selected=true]]:bg-primary/10 [&_[cmdk-item][aria-selected=true]]:text-foreground [&_[cmdk-item][aria-selected=true]_svg]:text-foreground';

/**
 * Keyboard-hint footer, kept out of the scroll area so it never scrolls away.
 * The navigate/select hints are hidden when there is nothing to act on (the
 * empty state), leaving only `esc` to dismiss.
 */
export function MentionMenuFooter({
  showNav = true,
  isSelect = false,
  canGoBack = false,
}: {
  showNav?: boolean;
  /** Enter applies the highlighted item (`select`) rather than opening a category (`open`). */
  isSelect?: boolean;
  /** Inside a category: Escape steps back instead of dismissing. */
  canGoBack?: boolean;
}) {
  return (
    <div
      data-testid="launch-mention-menu-hints"
      className="flex items-center gap-3 border-t border-border/50 bg-muted/20 px-2.5 py-1.5 text-[10px] text-muted-foreground/70"
    >
      {showNav ? (
        <>
          <span className="flex items-center gap-1">
            <Kbd size="xs">↑</Kbd>
            <Kbd size="xs">↓</Kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <Kbd size="xs">↵</Kbd>
            {isSelect ? 'select' : 'open'}
          </span>
        </>
      ) : null}
      <span className="ml-auto flex items-center gap-1">
        <Kbd size="xs">esc</Kbd>
        {canGoBack ? 'back' : 'dismiss'}
      </span>
    </div>
  );
}

/**
 * The agent launch `@`-mention menu. A cmdk `Command` (filtering disabled —
 * each provider owns its own query/ranking) that lazy-renders every
 * `launchMentionProviders` contribution inside its own `CommandGroup`. The
 * composer keeps focus, so this exposes an imperative {@link MentionMenuHandle}
 * (moveUp / moveDown / selectHighlighted) that drives cmdk's selection from the
 * outside: navigation sets cmdk's controlled `value`, selection dispatches
 * cmdk's own `cmdk-item-select` event on the highlighted row.
 */
export const LaunchMentionMenu = forwardRef<MentionMenuHandle, LaunchMentionMenuProps>(
  function LaunchMentionMenu(
    {
      query,
      style,
      attachments,
      selectedPersonaId,
      onApply,
      onClose,
      onResetQuery,
      onScopeCategory,
      onResultsChange,
      onRequestCreate,
      onRequestEdit,
      contextOnly,
      includeRuntimeConfig,
      repoSlug,
      agentId,
    },
    ref,
  ) {
    const { launchMentionProviders, agentContextProviders, chatMentionProviders } = usePlugins();
    const [value, setValue] = useState('');
    const [hasItems, setHasItems] = useState(false);
    const [listItemCount, setListItemCount] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    // Agent-context categories (PRs, tickets, issues, …) reference external
    // items rather than configure the run, so they appear in both surfaces.
    // Their components take the slimmer AgentContextProviderProps — adapt the
    // menu's provider props and thread the composer's repo through.
    const contextCategories = useMemo<MentionCategory[]>(
      () =>
        agentContextProviders.map((p) => ({
          id: p.id,
          label: p.label,
          keyword: p.keyword,
          Icon: p.icon,
          isContext: true,
          render: ({
            query: q,
            limit,
            onApply,
            onClose,
            groupHeading,
          }: LaunchMentionProviderProps) =>
            createElement(p.component, {
              query: q,
              limit,
              repoSlug,
              onApply,
              onClose,
              groupHeading,
            } satisfies AgentContextProviderProps),
        })),
      [agentContextProviders, repoSlug],
    );

    // Agent-scoped *actions* (start an assessment, …). The counterpart to the
    // context categories above: those reference an external item, these do
    // something to the agent that is already running — so they exist only in
    // `contextOnly` mode, where there is an agent to act on.
    const actionCategories = useMemo<MentionCategory[]>(
      () =>
        agentId
          ? chatMentionProviders.map((p) => ({
              id: p.id,
              label: p.label,
              Icon: p.icon,
              render: ({ query: q, limit, onApply, onClose, groupHeading }) =>
                createElement(p.component, {
                  agentId,
                  query: q,
                  limit,
                  onApply,
                  onClose,
                  groupHeading,
                } satisfies ChatMentionProviderProps),
            }))
          : [],
      [chatMentionProviders, agentId],
    );

    // Config categories that may also be applied to a running agent. Only
    // surfaced in context-only mode when the host opts in — the launch menu
    // renders every config provider through its own branch below. No
    // create/edit surfaces here: a running session attaches existing
    // servers; managing them stays on the launch and settings surfaces.
    const runtimeConfigCategories = useMemo<MentionCategory[]>(
      () =>
        contextOnly && includeRuntimeConfig
          ? launchMentionProviders
              .filter((p) => p.runtimeAttachable)
              .map((p) => ({
                id: p.id,
                label: p.label,
                keyword: p.keyword,
                Icon: p.icon,
                // `runtimeOnly` tells the provider to hide items that can
                // only apply at launch (e.g. local stdio MCP servers).
                render: (props: LaunchMentionProviderProps) =>
                  createElement(p.component, { ...props, runtimeOnly: true }),
              }))
          : [],
      [contextOnly, includeRuntimeConfig, launchMentionProviders],
    );

    // The categories shown at the top level. The chat's context-only menu
    // carries only context (agent runs + context categories) plus any
    // opted-in runtime-attachable config; the launch menu additionally
    // carries every run-config provider and the sandbox.
    const categories = useMemo<MentionCategory[]>(() => {
      const agentRuns: MentionCategory = {
        id: 'agent-runs',
        label: 'Agent runs',
        keyword: 'runs',
        Icon: Bot,
        isContext: true,
        render: (props: LaunchMentionProviderProps) => <AgentRunsMentionProvider {...props} />,
      };
      if (contextOnly)
        return [...actionCategories, ...runtimeConfigCategories, ...contextCategories, agentRuns];
      return [
        ...launchMentionProviders.map((p) => ({
          id: p.id,
          label: p.label,
          keyword: p.keyword,
          Icon: p.icon,
          render: (props: LaunchMentionProviderProps) => createElement(p.component, props),
          createLabel: p.createLabel,
          CreateComponent: p.createComponent
            ? (p.createComponent as ComponentType<LaunchMentionCreateProps>)
            : undefined,
          EditComponent: p.editComponent
            ? (p.editComponent as ComponentType<LaunchMentionEditProps>)
            : undefined,
        })),
        {
          id: 'sandbox',
          label: 'Sandbox',
          keyword: 'sandbox',
          Icon: Box,
          render: (props: LaunchMentionProviderProps) => <SandboxMentionProvider {...props} />,
        },
        ...contextCategories,
        agentRuns,
      ];
    }, [
      contextOnly,
      launchMentionProviders,
      contextCategories,
      actionCategories,
      runtimeConfigCategories,
    ]);

    // Two-level navigation derived purely from the query: `@repo:` scopes into
    // the Repositories category, so the visible token and the active category
    // stay in lockstep.
    const { scope, innerQuery } = useMemo(
      () => parseMentionScope(query, categories),
      [query, categories],
    );

    // Rewrite the composer token to `@<keyword>:`; the new query flows back in
    // and re-parses into a scope. The bare keyword is passed up — the host wraps
    // it with the trailing colon.
    const scopeInto = useCallback(
      (keyword: string | undefined) => {
        if (!keyword) return;
        setValue('');
        onScopeCategory?.(keyword);
      },
      [onScopeCategory],
    );

    // Binds a category's edit component to the host's request handler so a
    // provider row only ever sees a plain `onRequestEdit(itemId)`. Undefined
    // when the category ships no edit surface or the host hosts none — that
    // absence is the provider's signal to render no cog.
    const editHandlerFor = useCallback(
      (cat: MentionCategory) => {
        const EditComponent = cat.EditComponent;
        if (!EditComponent || !onRequestEdit || contextOnly) return undefined;
        return (itemId: string) => onRequestEdit({ EditComponent, itemId });
      },
      [onRequestEdit, contextOnly],
    );

    // Opens a category's create surface, seeded with a name. Drives both the
    // footer's "Add new …" row and the row-level `onRequestCreate` a provider
    // gets, so a provider can never offer a create path the menu itself is not
    // offering. Undefined when the category ships no create component or the
    // host hosts none.
    const createHandlerFor = useCallback(
      (cat: MentionCategory) => {
        const { CreateComponent, createLabel } = cat;
        if (!CreateComponent || !createLabel || !onRequestCreate || contextOnly) return undefined;
        return (defaultName: string) =>
          onRequestCreate({ CreateComponent, label: createLabel, defaultName });
      },
      [onRequestCreate, contextOnly],
    );

    const openScopeCreate = scope ? createHandlerFor(scope) : undefined;

    const goBack = useCallback(() => {
      setValue('');
      onResetQuery?.();
    }, [onResetQuery]);

    // At the root level, typing searches the *items* of the flattened
    // categories, not the category labels. In the launch menu that is every
    // config + context category; agent runs stay reachable only by scoping
    // into their category (they are context, not run config, and would drown
    // the config hits). In the context-only chat menu agent runs ARE the
    // primary content, so they join the flattened search there.
    const searchableCategories = useMemo(
      () => (contextOnly ? categories : categories.filter((c) => c.id !== 'agent-runs')),
      [contextOnly, categories],
    );
    const rootSearch = !scope && query.trim().length > 0;
    // Only a scoped view carries the ceiling, and hitting it means the source
    // had more to give.
    const isCapped = (Boolean(scope) || Boolean(contextOnly)) && listItemCount >= CATEGORY_LIMIT;

    // Categories whose keyword (prefix) or label (substring) matches the root
    // query, surfaced above the flattened item results so `@rep` suggests the
    // Repositories category before its items.
    const matchedCategories = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return [];
      return categories.filter(
        (c) => c.keyword?.toLowerCase().startsWith(q) || c.label.toLowerCase().includes(q),
      );
    }, [categories, query]);

    const getValidItems = useCallback((): HTMLElement[] => {
      const root = containerRef.current;
      if (!root) return [];
      return Array.from(root.querySelectorAll<HTMLElement>(VALID_ITEM_SELECTOR));
    }, []);

    // cmdk in controlled mode does not auto-highlight, so keep a valid row
    // selected as the query changes and as async provider results stream in.
    const ensureSelection = useCallback(() => {
      const values = getValidItems().map((el) => el.getAttribute('data-value') ?? '');
      const hasResults = values.length > 0;
      setHasItems(hasResults);
      onResultsChange?.(hasResults);
      // Count list rows only: the create row is a cmdk item outside the list,
      // so counting the whole menu would claim truncation a row early.
      const list = containerRef.current?.querySelector(LIST_SELECTOR);
      setListItemCount(list ? list.querySelectorAll(VALID_ITEM_SELECTOR).length : 0);
      if (values.length === 0) return;
      setValue((prev) => (values.includes(prev) ? prev : values[0]));
    }, [getValidItems, onResultsChange]);

    // Runs after every render (initial mount + query/provider prop changes).
    // Deferred a frame so it reads cmdk's freshly committed rows and doesn't
    // call setState synchronously inside the effect body.
    useEffect(() => {
      const raf = requestAnimationFrame(ensureSelection);
      return () => cancelAnimationFrame(raf);
    });

    // Child providers resolve their data asynchronously and re-render
    // independently of this parent, so watch the DOM to repair selection when
    // rows appear or disappear.
    useEffect(() => {
      const root = containerRef.current;
      if (!root) return;
      const observer = new MutationObserver(() => ensureSelection());
      observer.observe(root, { childList: true, subtree: true });
      return () => observer.disconnect();
    }, [ensureSelection]);

    const move = useCallback(
      (dir: 1 | -1) => {
        const items = getValidItems();
        if (items.length === 0) return;
        const current = items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
        let next = (current < 0 ? (dir === 1 ? -1 : 0) : current) + dir;
        if (next < 0) next = items.length - 1;
        if (next >= items.length) next = 0;
        const target = items[next];
        setValue(target.getAttribute('data-value') ?? '');
        target.scrollIntoView({ block: 'nearest' });
      },
      [getValidItems],
    );

    useImperativeHandle(
      ref,
      () => ({
        moveUp: () => move(-1),
        moveDown: () => move(1),
        selectHighlighted: () => {
          const root = containerRef.current;
          if (!root) return false;
          const selected =
            root.querySelector<HTMLElement>(SELECTED_ITEM_SELECTOR) ?? getValidItems()[0] ?? null;
          if (!selected) return false;
          // Mirror cmdk's own Enter handling: dispatch the select event the
          // item registered a native listener for (works regardless of focus).
          selected.dispatchEvent(new Event('cmdk-item-select'));
          return true;
        },
        // Inside a scoped category, Escape steps back to the root (and the
        // composer keeps the menu open); at the top level it is not consumed.
        escape: () => {
          if (scope) {
            goBack();
            return true;
          }
          return false;
        },
      }),
      [move, getValidItems, scope, goBack],
    );

    // A selectable category row. Used both in the full category list and in the
    // hybrid root view's "Categories" group. Selecting scopes into the category
    // by rewriting the composer token to `@<keyword>:`.
    const renderCategoryRow = (cat: MentionCategory) => (
      <CommandItem
        key={cat.id}
        value={`category:${cat.id}`}
        keywords={cat.keyword ? [cat.label, cat.keyword] : [cat.label]}
        data-testid={`launch-mention-category-${cat.id}`}
        onSelect={() => scopeInto(cat.keyword)}
      >
        <span className="flex w-full items-center gap-2">
          <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-3.5">
            <cat.Icon />
          </span>
          <span className="min-w-0 flex-1 truncate">{cat.label}</span>
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/40" />
        </span>
      </CommandItem>
    );

    return (
      <div
        ref={containerRef}
        style={style}
        data-testid="launch-mention-menu"
        // Deliberately unclipped: this box matches the popover's, so hiding
        // overflow would cut off its drop shadow. The popover clips its content.
        className="flex min-h-0 flex-col"
      >
        <Command
          shouldFilter={false}
          value={value}
          onValueChange={setValue}
          className={cn(MENTION_MENU_CLASS, 'flex min-h-0 flex-1 flex-col overflow-hidden')}
        >
          {scope ? (
            <Button
              variant="ghost"
              onClick={goBack}
              data-testid="launch-mention-back"
              className="h-auto w-full justify-start gap-1 rounded-none border-b border-border/50 px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
            >
              <ChevronLeft className="size-3.5" />
              <span className="truncate">{scope.label}</span>
            </Button>
          ) : null}
          {/* Roughly ten rows. Back, create and footer add ~95px on top, so
              raising this makes the menu loom over the dialog. */}
          <CommandList className="min-h-0 max-h-[240px] flex-1 scroll-py-1 overflow-y-auto p-1">
            <CommandEmpty className="py-6 text-center text-xs text-muted-foreground">
              No matches.
            </CommandEmpty>
            {scope ? (
              <>
                <Suspense fallback={null}>
                  {scope.render({
                    query: innerQuery,
                    attachments,
                    selectedPersonaId,
                    onApply,
                    onClose,
                    onRequestEdit: editHandlerFor(scope),
                    onRequestCreate: createHandlerFor(scope),
                    limit: CATEGORY_LIMIT,
                  })}
                </Suspense>
              </>
            ) : rootSearch ? (
              // Root-level search: surface matching categories first (so `@rep`
              // suggests Repositories), then flatten every searchable category's
              // items so typing searches all of them at once. Providers render
              // nothing when they have no matches, so only relevant groups show.
              <>
                {matchedCategories.length > 0 ? (
                  <CommandGroup heading="Categories">
                    {matchedCategories.map(renderCategoryRow)}
                  </CommandGroup>
                ) : null}
                <Suspense fallback={null}>
                  {searchableCategories.map((cat) => (
                    <Fragment key={cat.id}>
                      {cat.render({
                        query,
                        attachments,
                        selectedPersonaId,
                        onApply,
                        onClose,
                        onRequestEdit: editHandlerFor(cat),
                        onRequestCreate: createHandlerFor(cat),
                        limit: ROOT_SEARCH_LIMIT,
                        // Group each provider's hits under its category name so
                        // the flattened results stay legible across sources.
                        groupHeading: cat.label,
                      })}
                    </Fragment>
                  ))}
                </Suspense>
              </>
            ) : contextOnly ? (
              // Context-only root: recent agent runs lead (the common case is
              // referencing another run), with the context categories listed
              // below so `@pr:` / `@linear:` / … stay discoverable.
              <>
                <Suspense fallback={null}>
                  <AgentRunsMentionProvider
                    query={query}
                    attachments={attachments}
                    selectedPersonaId={selectedPersonaId}
                    onApply={onApply}
                    onClose={onClose}
                    limit={CATEGORY_LIMIT}
                  />
                </Suspense>
                {runtimeConfigCategories.length + contextCategories.length > 0 ? (
                  <CommandGroup heading="Categories">
                    {[...runtimeConfigCategories, ...contextCategories].map(renderCategoryRow)}
                  </CommandGroup>
                ) : null}
              </>
            ) : (
              <CommandGroup>{categories.map(renderCategoryRow)}</CommandGroup>
            )}
          </CommandList>
          {scope && openScopeCreate && scope.createLabel ? (
            // "Add new …" row. Outside the list so a long category cannot push
            // it out of reach, and rendered even when the provider yields
            // nothing so an empty category still offers creation. Selecting it
            // hands the component up to the host, which mounts it outside this
            // (soon-to-unmount) menu.
            <CommandGroup className="border-t border-border/50 pt-1">
              <CommandItem
                value={`create:${scope.id}`}
                keywords={[scope.createLabel]}
                data-testid={`launch-mention-create-${scope.id}`}
                onSelect={() => openScopeCreate(innerQuery)}
              >
                <span className="flex w-full items-center gap-2 text-muted-foreground">
                  <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-3.5">
                    <Plus />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{scope.createLabel}</span>
                </span>
              </CommandItem>
            </CommandGroup>
          ) : null}
          {isCapped ? (
            // The one case where rows exist that no amount of scrolling
            // reaches, so say so rather than let the list look complete.
            <div
              data-testid="launch-mention-menu-note"
              className="border-t border-border/50 bg-muted/20 px-2.5 py-1 text-[10px] text-muted-foreground/70"
            >
              showing first {CATEGORY_LIMIT}, type to narrow
            </div>
          ) : null}
          <MentionMenuFooter
            showNav={hasItems}
            isSelect={contextOnly || Boolean(scope) || rootSearch}
            canGoBack={Boolean(scope)}
          />
        </Command>
      </div>
    );
  },
);
